import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Divider,
  Grid,
  TextInput,
  Button,
  ActionIcon,
  Tooltip,
  Group,
  Box,
  Text,
  Table,
} from '@mantine/core';
import { IconSearch, IconPrinter } from '@tabler/icons-react';

import * as imovelActions from '../../../actions/Lote';
import InnerView from './InnerView';
import RelatorioUnidade from './RelatorioUnidade';
import ViewerList from '../../../components/SmartForm/Viewer/List';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import type { Unidade } from '../../../store/types';
import { TableSkeleton } from '../../../components/TableSkeleton';
import EmptyState from '../../../components/EmptyState';
import { useOptionalNavegadorMap } from '../../../components/Navegador/NavegadorMapContext';

interface InnerListProps {
  loteId: number;
}

interface LocationStateWithFilter { filterText?: string }

export default function InnerList({ loteId }: InnerListProps) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const loading = useAppSelector((state) => state.container.loading);
  const current = useAppSelector((state) => state.lote.current);
  const results = useAppSelector((state) => state.lote.innerResults);
  const inShell = !!useOptionalNavegadorMap();

  const [searchInputValue, setSearchInputValue] = useState('');
  const [filterText, setFilterText] = useState('');
  const [pageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [imovelSelecionado, setImovelSelecionado] = useState<number | null>(null);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [placeHolder, setPlaceHolder] = useState('');

  const loadInnerList = useCallback(
    (overridePage: number = page, overrideFilter: string = filterText) => {
      dispatch((imovelActions as any).innerList(
        {
          page: overridePage,
          pageSize,
          ORDERCOLUMN: 'INSCRICAO',
          ORDERASC: true,
          filters: { SEARCH: overrideFilter, LOTE_ID: loteId },
        },
        ({ data }: { data: { items: Unidade[] } | null }) => {
          if (overridePage === 1 && data && data.items && data.items.length > 0) {
            setExpandedRows([data.items[0].id]);
          }
        },
      ));
    },
    [dispatch, loteId, pageSize],
  );

  useEffect(() => {
    const initialFilterText = (location.state as LocationStateWithFilter | null)?.filterText || '';
    setSearchInputValue(initialFilterText);
    setFilterText(initialFilterText);
    loadInnerList(1, initialFilterText);

    dispatch((imovelActions as any).getPlaceHolderByModule((x: { data: string }) => {
      setPlaceHolder(x.data);
    }));
  }, []);

  useEffect(() => {
    setPage(1);
    setSearchInputValue('');
    setFilterText('');
    loadInnerList(1, '');
  }, [loteId]);

  const clickSearch = () => {
    const trimmed = (searchInputValue || '').trim();
    setPage(1);
    setFilterText(trimmed);
    loadInnerList(1, trimmed);
  };

  const pageChange = (newPage: { current: number }) => {
    setPage(newPage.current);
    loadInnerList(newPage.current, filterText);
  };

  const renderRowResult = (item: Unidade) => (
    <InnerView key={item.id} unidadeId={item.id} />
  );

  const renderRowActions = (item: Unidade) => (
    <Tooltip label="Imprimir">
      <ActionIcon variant="subtle" aria-label="Imprimir relatório" onClick={() => setImovelSelecionado(item.id)}>
        <IconPrinter size={16} />
      </ActionIcon>
    </Tooltip>
  );

  return (
    <Box component="form">
      <Divider my="md" />

      <Grid>
        <Grid.Col span={{ base: 12, md: inShell ? 12 : 7 }} offset={{ base: 0, md: inShell ? 0 : 2 }}>
          <TextInput
            disabled={loading}
            autoFocus
            maxLength={300}
            placeholder={placeHolder}
            leftSection={<IconSearch size={16} />}
            value={searchInputValue || ''}
            onChange={(e) => setSearchInputValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') clickSearch(); }}
            rightSectionWidth={92}
            rightSection={
              <Button size="xs" onClick={clickSearch} leftSection={<IconSearch size={14} />}>
                Buscar
              </Button>
            }
          />
        </Grid.Col>
      </Grid>

      {results && current && (
        <Group justify="space-between" mt="xs" px="md">
          <Text fw={600}>{filterText && `Filtrado por "${filterText}"`}</Text>
          <Text size="sm" c="dimmed">
            Listando {results.total} de {current.unidadesCount} {current.unidadesCount === 1 ? 'unidade' : 'unidades'}.
          </Text>
        </Group>
      )}

      <Box mt="md">
        {loading && (!results || results.items.length === 0) ? (
          <Table><Table.Tbody><TableSkeleton columns={['82%', '72%']} /></Table.Tbody></Table>
        ) : results && results.items.length === 0 ? (
          <EmptyState
            label={filterText ? 'Nenhuma unidade encontrada para o filtro' : 'Nenhuma unidade cadastrada neste lote'}
            mih={180}
          />
        ) : results ? (
          <ViewerList
            type="table"
            columns={1}
            items={results.items}
            fieldNameIdentifier="id"
            url="/navegador-geofinanceiro/lote/{id}"
            rowActionsRender={renderRowActions}
            pagination={{ pageSize, total: results.total, current: page }}
            expandedRowKeys={expandedRows}
            onExpandedRowsChange={setExpandedRows}
            expandedRowRender={renderRowResult}
            onChange={pageChange}
          />
        ) : null}
      </Box>

      {imovelSelecionado && (
        <RelatorioUnidade
          unidadeId={imovelSelecionado}
          onClose={() => setImovelSelecionado(null)}
        />
      )}
    </Box>
  );
}
